import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import { VettingRoutes } from "../../constants/vetting/vetting.constants";
import {
    SaveVetResponse,
    VetModel,
    VetClientModel,
    VetTaskClientModel,
    SubtaskCorrespondenceEmailModel, VetRecentActivityClientModel, VettingSireReport, VettingSireReportType, VetRuleRunClientModel, VetRuleRunItemClientModel, VetRuleRunRiskClientModel,
    OpenVetModel
} from "../../models/vetting/vetting.models";
import { GetCapResponse, GetListItemResponse } from "../../models/lists/list.models";
import { BusinessUnitVetAlertRecipientModel } from "../../models/customers/customer.models";
import { BaseValidationResponse } from "../../models/validations/validations.models";
import { UpdateTaskOutcomeResponse } from "../../models/rightship-inspections/rightship-inspections.models";
import { Claims } from "../../constants/authentication/claims";
import { UserClaimsService } from "../authentication/user-claims.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { Sire2ReportResponse } from "../../models/vetting/sire.models";

@autoinject
export class VettingService {
    constructor(private requestClient: RequestClientService, private userClaimsService: UserClaimsService,
        private configService: ConfigService) {
    }

    public saveVetCorrespondenceEmails(vetId: number, vetCorrespondenceEmails: SubtaskCorrespondenceEmailModel[]): Promise<void> {
        return this.requestClient.post(VettingRoutes.SaveVetCorrespondenceEmails, {}, {
            vetCorrespondenceEmails: vetCorrespondenceEmails,
            vetId: vetId
        });
    }

    public setCustomerDecision(vetId: number, customerDecisionStatusId: number, comments: string): Promise<void> {
        return this.requestClient.post(VettingRoutes.SetCustomerDecision, {}, {
            customerDecisionStatusId: customerDecisionStatusId,
            vetId: vetId,
            comments: comments
        });
    }

    public saveVet(manualDischargePort: boolean, manualLoadPort: boolean, dischargePortCountryId: number,
        loadPortCountryId: number, dischargePort: string, loadPort: string, vet: VetModel,
        vetCorrespondenceEmails: SubtaskCorrespondenceEmailModel[]): Promise<SaveVetResponse> {
        return this.requestClient.post(VettingRoutes.SaveVet, {}, {
            vetCorrespondenceEmails: vetCorrespondenceEmails,
            manualDischargePort: manualDischargePort,
            manualLoadPort: manualLoadPort,
            dischargePortCountryId: dischargePortCountryId,
            loadPortCountryId: loadPortCountryId,
            dischargePort: dischargePort,
            loadPort: loadPort,
            vet: vet
        });
    }

    public getBusinessUnitVetAlertRecipients(vetId: number, vettingActionId: number, onboardingConfigId: number = null): Promise<BusinessUnitVetAlertRecipientModel[]> {
        return this.requestClient.get(`${VettingRoutes.GetBusinessUnitVetAlertRecipients}/${vetId}/${vettingActionId}/${onboardingConfigId}`, {});
    }

    public getVetRuleRunsClientModel(id: number): Promise<VetRuleRunClientModel[]> {
        return this.requestClient.get(`${VettingRoutes.GetVetRuleRunsClientModel}/${id}`, {});
    }

    public getVetRuleRunItemsClientModel(vettingRuleRunId: number, previousVettingRuleRunId: number): Promise<VetRuleRunItemClientModel[]> {
        return this.requestClient.get(`${VettingRoutes.GetVetRuleRunItemsClientModel}`, { vettingRuleRunId, previousVettingRuleRunId });
    }

    public getVetRuleRunItemRiskClientModel(vettingRuleRunId: number): Promise<VetRuleRunRiskClientModel[]> {
        return this.requestClient.get(`${VettingRoutes.GetVetRuleRunItemRiskClientModel}`, { vettingRuleRunId });
    }

    public getVet(id: number, includeVetTasks: boolean): Promise<VetClientModel> {
        return this.requestClient.get(`${VettingRoutes.GetVet}/${id}`, { includeVetTasks });
    }

    public getVetCorrespondenceEmails(vetId: number): Promise<[]> {
        return this.requestClient.get(`${VettingRoutes.GetVetCorrespondenceEmails}`, { vetId });
    }

    public getPreviouslyUsedShipManagerEmail(vetId: number): Promise<{ docVettingContactEmails: { email: string, isPrimary: boolean }[], previousVetEmails: string[], vpqEmail: string, docEmail: string }> {
        return this.requestClient.get(`${VettingRoutes.GetPreviouslyUsedShipManagerEmail}/${vetId}`, {});
    }

    public getVetTasks(id: number): Promise<VetTaskClientModel[]> {
        return this.requestClient.get(`${VettingRoutes.GetVetTasks}/${id}`, {});
    }

    public updateTaskOutcome(vetTaskId: number, outcome: number): Promise<UpdateTaskOutcomeResponse> {
        return new Promise<UpdateTaskOutcomeResponse>((resolve, reject) => {
            return this.requestClient.post<UpdateTaskOutcomeResponse>(`${VettingRoutes.UpdateVetTaskOutcome}/${vetTaskId}`, {}, {
                outcome: outcome
            }).then(result => {
                if (result.validationResult && result.validationResult.hasError) {
                    reject(result);
                }
                else {
                    resolve(result);
                }
            },

                (e) => {
                    reject(e);
                });
        })
    }

    changeVetApplicable(cfrId: number, vetTaskId: number, vetApplicable: boolean): Promise<void> {
        return this.requestClient.post(`${VettingRoutes.ChangeVetApplicable}`, {}, {
            cfrId, vetTaskId, vetApplicable
        });
    }

    public changeStatus(id: number, vetStatusId: number, comments: string, meta: string, subtaskTypes: number[], statusSnippet: string,
        reopenStatusReasonId: number, failedRightShipCriteria: boolean, failedCustomerCriteria: boolean, cancellationStatusReasonId: number): Promise<void> {
        return this.requestClient.post(VettingRoutes.ChangeStatus, {}, {
            id: id,
            comments: comments,
            statusId: vetStatusId,
            meta: meta,
            subtaskTypes: subtaskTypes,
            statusSnippet: statusSnippet,
            reopenedReasonId: reopenStatusReasonId,
            cancellationStatusReasonId: cancellationStatusReasonId,
            failedRightShipCriteria: failedRightShipCriteria,
            failedCustomerCriteria: failedCustomerCriteria
        });
    }

    public applyForWaiver(id: number, comments: string): Promise<void> {
        return this.requestClient.post(VettingRoutes.ApplyForWaiver, {}, {
            id: id,
            comments: comments
        });
    }

    public getListItems(vetTaskId: number): Promise<GetListItemResponse[]> {
        return this.requestClient.get<GetListItemResponse[]>(`${VettingRoutes.GetListItems}/${vetTaskId}`, {});
    }

    public getCapResponse(vetId: number, vetTaskId: number): Promise<GetCapResponse> {
        return this.requestClient.get<GetCapResponse>(`${VettingRoutes.GetCapResponse}/${vetId}/${vetTaskId}`, {});
    }

    public rerunRules(vetId: number): Promise<void> {
        return this.requestClient.post(`${VettingRoutes.ReRunRules}/${vetId}`, {}, {});
    }

    public saveComments(vetId: number, taskComments: { passedVettingComment: string; failedVettingComment: string; infoVettingComment: string; naVettingComment: string; vetTaskId: number; forceIncludeComment: boolean }[], otherComments: string): Promise<void> {
        return this.requestClient.post(`${VettingRoutes.SaveVettingComments}`, {}, {
            vetId: vetId,
            taskComments: taskComments,
            otherComments: otherComments
        });
    }

    saveVetTask(vetId: number, request: { meta: string; title: string; description: string; actionToTake: string; ruleActionId: number; }): Promise<BaseValidationResponse> {
        return this.requestClient.post(`${VettingRoutes.SaveVetTask}/${vetId}`, {}, request);
    }

    getVetRecentActivity(vetId: number, onlyOpen: boolean, hideInsp: boolean): Promise<VetRecentActivityClientModel[]> {
        return this.requestClient.get<VetRecentActivityClientModel[]>(`${VettingRoutes.RecentActivity}`, {
            vetId: vetId,
            onlyOpen: onlyOpen,
            hideInsp: hideInsp
        });
    }

    public getVetRequiresMoreInformationEmails(businessUnitId: number, vesselTypeId: number): Promise<SubtaskCorrespondenceEmailModel[]> {
        return this.requestClient.get<SubtaskCorrespondenceEmailModel[]>(`${VettingRoutes.GetVetRequiresMoreInformationEmails}/${businessUnitId}/${vesselTypeId}`, {});
    }

    getVettingSireReport(id: number): Promise<VettingSireReport> {
        return this.requestClient.get<VettingSireReport>(`${VettingRoutes.GetVettingSireReport}/${id}`, {});
    }

    getVettingSire2Report(id: number): Promise<Sire2ReportResponse> {
        return this.requestClient.get<Sire2ReportResponse>(`Vet/GetVettingSire2Report/${id}`, {});
    }

    getVettingSireReportAnswers(id: number, type: VettingSireReportType): Promise<string> {
        return this.requestClient.get<string>(`${VettingRoutes.GetVettingSireReportAnswers}`, {
            vettingSireReportId: id,
            vettingSireReportTypeId: type
        });
    }

    deleteCorrespondeceEmail(id: number): Promise<any> {
        return this.requestClient.post(`${VettingRoutes.DeleteSubtaskCorrespondence}`, null, { subtaskCorrespondeceEmailId: id });
    }

    updateCorrespondeceEmail(id: number, email: string): Promise<any> {
        return this.requestClient.post(`${VettingRoutes.UpdateSubtaskEmailCorrespondenceEmail}`, null, { subtaskCorrespondeceEmailId: id, email: email });
    }

    getSireQuestionsJson(): Promise<any[]> {
        return this.requestClient.get<any[]>(new URL("sire-questions.json", this.configService.config.urls.rightShipPlatformApiUrl).toString(), {});
    }

    isLink(type: string) {
        if (type?.toLowerCase() == 'psc') {
            return this.userClaimsService.hasPermission(Claims.CanAccessPSCs);
        } else if (type?.toLowerCase() == 'inc') {
            return this.userClaimsService.hasPermission(Claims.CanAccessINCs);
        } else if (type?.toLowerCase() == 'insp') {
            return this.userClaimsService.hasPermission(Claims.CanAccessRightShipInspections);
        } else if (type?.toLowerCase() == 'ghg') {
            return this.userClaimsService.hasPermission(Claims.CanAccessEnvironmentals);
        } else if (type?.toLowerCase() == 'tvq') {
            return this.userClaimsService.hasPermission(Claims.CanAccessForms);
        } else if (type?.toLowerCase() == 'rfi') {
            return this.userClaimsService.hasPermission(Claims.RightShipUser);
        } else if (type?.toLowerCase() == 'cfr') {
            return this.userClaimsService.hasPermission(Claims.CanAccessCFRs);
        }

        return true;
    }

    generateRecentLink(vetId: number, title: string, id: number, type: string) {
        const titleFormatted = title ? title.toLowerCase() : null;
        const typeFormatted = type ? type.toLowerCase() : null;

        if (titleFormatted == 'rfi') {
            return `vet/${vetId}?subtaskId=${id}`;
        }
        else if (titleFormatted == 'tq' || titleFormatted == 'moc' || titleFormatted == 'class status' || titleFormatted == 'hq' || titleFormatted == 'vessel application form' || typeFormatted == 'class-status') {
            return `/vet-task-onboarding-custom-content?vesselApplicationFormId=${id}`;
        }
        else if (titleFormatted == 'inc') {
            return `/incident/${id}`
        }
        else if (titleFormatted == 'environmental verification') {
            return `/environmental-review-request/${id}`
        }
        return `/${titleFormatted}/${id}`;
    }

    getOpenVetsForVessel(vesselId: number): Promise<OpenVetModel[]> {
        return this.requestClient.get<OpenVetModel[]>(`${VettingRoutes.GetOpenVetsForVessel}/${vesselId}`);
    }
}
